import { motion, AnimatePresence } from 'framer-motion';
import { useState, useRef, useEffect } from 'react';
import DOMPurify from 'dompurify';
import css from '../../style.module.css';

// props 
// 1-label => assignment.label
// 2-taskFields => assignment.taskFields

const EMAIL_SPACER_RE = /^(?:\s|&nbsp;|\u00a0|&#160;)*$/i;

const clampCssSize = (value, maxValue) => {
    if (!value) return value;
    const match = String(value).match(/^(\d+(?:\.\d+)?)px$/i);
    if (!match) return value;
    const number = Number(match[1]);
    return number > maxValue ? `${maxValue}px` : value;
};

const normalizeAssignmentHtml = (htmlString) => {
    const parser = new DOMParser();
    const doc = parser.parseFromString(DOMPurify.sanitize(htmlString), 'text/html');

    doc.body.querySelectorAll('*').forEach(el => {
        const tagName = el.tagName.toLowerCase();

        if (['script', 'style', 'meta', 'link'].includes(tagName)) {
            el.remove();
            return;
        }

        el.removeAttribute('height');
        el.removeAttribute('vspace');
        el.removeAttribute('hspace');
        el.removeAttribute('valign');

        if (el.style) {
            el.style.minHeight = '';
            el.style.height = '';
            el.style.lineHeight = clampCssSize(el.style.lineHeight, 28);

            if (tagName !== 'td' && tagName !== 'th') {
                el.style.marginTop = clampCssSize(el.style.marginTop, 16);
                el.style.marginBottom = clampCssSize(el.style.marginBottom, 16);
                el.style.paddingTop = clampCssSize(el.style.paddingTop, 16);
                el.style.paddingBottom = clampCssSize(el.style.paddingBottom, 16);
            }
        }

        const color = el.style.color;
        if (!color) return;

        const temp = document.createElement('div');
        temp.style.color = color;
        document.body.appendChild(temp);
        const computed = window.getComputedStyle(temp).color;
        document.body.removeChild(temp);

        const match = computed.match(/\d+/g);
        if (!match) return;
        const [r, g, b] = match.map(Number);

        // Calculate lightness (0-255)
        const lightness = (r * 0.299 + g * 0.587 + b * 0.114);

        if (lightness > 180) {
            el.style.color = 'var(--ink)';
        }
    });

    doc.body.querySelectorAll('img').forEach(img => {
        const src = img.getAttribute('src') || '';
        const width = Number(img.getAttribute('width') || 0);
        const height = Number(img.getAttribute('height') || 0);
        const alt = img.getAttribute('alt') || '';

        if (!src || src.startsWith('cid:') || (!alt && width <= 1 && height <= 1)) {
            img.remove();
            return;
        }

        img.removeAttribute('height');
        img.style.maxWidth = '100%';
        img.style.height = 'auto';
    });

    doc.body.querySelectorAll('br').forEach(br => {
        let next = br.nextSibling;
        let count = 0;

        while (next && next.nodeName === 'BR') {
            const extraBreak = next;
            next = next.nextSibling;
            count += 1;
            if (count > 1) extraBreak.remove();
        }
    });

    Array.from(doc.body.querySelectorAll('tr, p, div')).forEach(el => {
        const hasMeaningfulMedia = el.querySelector('img, a, table');
        const text = el.textContent || '';
        if (!hasMeaningfulMedia && EMAIL_SPACER_RE.test(text)) {
            el.remove();
        }
    });

    return doc.body.innerHTML;
};

function TaskField({ fieldName, fieldValue, containsHTML }) {
    const [isExpanded, setIsExpanded] = useState(true);
    const [needsExpand, setNeedsExpand] = useState(false);
    const valueRef = useRef(null);

    const isHtml = fieldValue ? containsHTML(fieldValue) : false;

    useEffect(() => {
        if (valueRef.current) {
            setNeedsExpand(valueRef.current.scrollHeight > valueRef.current.clientHeight);
        }
    }, [fieldValue]);

    const toggle = (e) => {
        e.stopPropagation();
        if (needsExpand || isExpanded) setIsExpanded(prev => !prev);
    };

    return (
        <div className="d-flex flex-column">

            {/* Field Name — chevron only when content overflows */}
            <div
                className="mb-1 d-flex align-items-center justify-content-between"
                onClick={toggle}
                style={{
                    cursor: needsExpand || isExpanded ? 'pointer' : 'default',
                    color: 'var(--ink-soft)',
                    fontSize: '0.8rem',
                    fontWeight: '600',
                    textTransform: 'uppercase',
                    letterSpacing: '0.5px',
                    userSelect: 'none'
                }}
            >
                <span>{fieldName}</span>

                {/* Only show chevron if there's overflow content */}
                {(needsExpand || isExpanded) && (
                    <motion.i
                        className="fas fa-chevron-down"
                        style={{ color: 'var(--accent)', fontSize: '0.65rem', marginLeft: '6px' }}
                        animate={{ rotate: isExpanded ? 180 : 0 }}
                        transition={{ duration: 0.25 }}
                    />
                )}
            </div>

            {/* Field Value */}
            <div
                ref={valueRef}
                className="p-3 rounded-3"
                // onClick={toggle}
                style={{
                    backgroundColor: 'var(--surface-sunken)',
                    border: `1px solid ${isExpanded ? 'var(--accent)' : 'var(--hairline)'}`,
                    color: 'var(--ink)',
                    fontSize: '0.95rem',
                    lineHeight: isHtml ? '1.45' : '1.6',
                    wordBreak: 'break-word',
                    whiteSpace: isHtml ? 'normal' : 'pre-wrap',
                    // cursor: needsExpand || isExpanded ? 'pointer' : 'default',
                    position: 'relative',
                    maxHeight: isExpanded ? (isHtml ? '720px' : 'none') : '90px',
                    overflow: isExpanded && isHtml ? 'auto' : isExpanded ? 'visible' : 'hidden',
                    transition: 'border-color 0.25s ease'
                }}
            >
                {fieldValue ? (
                    isHtml ? (
                        <div
                            className={css.assignmentHtmlContent}
                            dangerouslySetInnerHTML={{ __html: normalizeAssignmentHtml(fieldValue) }}
                        />
                    ) : (
                        <div style={{ whiteSpace: 'pre-wrap', wordBreak: 'break-word' }}>
                            {fieldValue}
                        </div>
                    )
                ) : (
                    <span className="text-muted fst-italic">No value</span>
                )}

                {/* Fade gradient — only when collapsed AND content actually overflows */}
                {!isExpanded && needsExpand && (
                    <div
                        style={{
                            position: 'absolute',
                            bottom: 0, left: 0, right: 0,
                            height: '32px',
                            background: 'linear-gradient(to bottom, transparent, var(--surface))',
                            pointerEvents: 'none',
                            borderRadius: '0 0 10px 10px'
                        }}
                    />
                )}
            </div>

            {/* Show more / Show less — only when content actually overflows */}
            {(needsExpand || isExpanded) && fieldValue && (
                <div
                    onClick={toggle}
                    style={{
                        color: 'var(--accent)',
                        fontSize: '0.72rem',
                        fontWeight: '700',
                        cursor: 'pointer',
                        marginTop: '4px',
                        userSelect: 'none',
                        width: 'fit-content'
                    }}
                >
                    {isExpanded ? 'Show less ↑' : 'Show more ↓'}
                </div>
            )}
        </div>
    );
}
export default function AssignmentTaskFields({ label, taskFields }) {
    const [isTaskFieldsExpanded, setIsTaskFieldsExpanded] = useState(true);

    const containsHTML = (value) => {
        if (typeof value !== 'string') return false;
        return /<[^>]+>/.test(value);
    };

    return (
        <motion.div
            className="my-3 rounded-3 overflow-hidden"
            style={{ border: '1px solid var(--hairline)', backgroundColor: 'white' }}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 }}
        >
            {/* Expandable Header */}
            <motion.div
                className="p-4 d-flex align-items-center justify-content-between"
                style={{
                    cursor: 'pointer',
                    backgroundColor: 'var(--surface-sunken)',
                    borderBottom: isTaskFieldsExpanded ? '1px solid var(--hairline)' : 'none'
                }}
                onClick={() => setIsTaskFieldsExpanded(!isTaskFieldsExpanded)}
                whileHover={{ backgroundColor: 'var(--surface-sunken)' }}
            >
                <div className="d-flex align-items-center">
                    <div
                        className="d-flex align-items-center justify-content-center me-3"
                        style={{
                            width: '40px',
                            height: '40px',
                            borderRadius: '10px',
                            background: 'var(--accent-soft)'
                        }}
                    >
                        <i className="fas fa-database" style={{ color: 'var(--accent)', fontSize: '1.1rem' }}></i>
                    </div>
                    <h5 className="m-0" style={{ color: 'var(--ink)', fontWeight: '700' }}>
                        {label}
                    </h5>
                </div>

                <motion.i
                    className="fas fa-chevron-down"
                    style={{ color: 'var(--accent)', fontSize: '1rem' }}
                    animate={{ rotate: isTaskFieldsExpanded ? 180 : 0 }}
                    transition={{ duration: 0.3 }}
                />
            </motion.div>

            {/* Expandable Content */}
            <AnimatePresence>
                {isTaskFieldsExpanded && (
                    <motion.div
                        initial={{ height: 0, opacity: 0 }}
                        animate={{ height: 'auto', opacity: 1 }}
                        exit={{ height: 0, opacity: 0 }}
                        transition={{ duration: 0.3 }}
                        style={{ overflow: 'hidden' }}
                    >
                        <div className="p-4">
                            {taskFields.map((record, recordIndex) => (
                                <div key={recordIndex} className="mb-4">

                                    {/* Record Badge */}
                                    {taskFields.length > 1 && (
                                        <div
                                            className="badge mb-3"
                                            style={{
                                                backgroundColor: 'rgba(199, 29, 150, 0.1)',
                                                color: 'var(--accent)',
                                                fontSize: '0.75rem',
                                                fontWeight: '600',
                                                padding: '4px 10px',
                                                borderRadius: '6px'
                                            }}
                                        >
                                            Record {recordIndex + 1}
                                        </div>
                                    )}

                                    {/* Fields */}
                                    <div className="d-flex flex-column gap-3">
                                        {Object.entries(record).map(([fieldName, fieldValue], fieldIndex) => (
                                            <TaskField
                                                key={fieldIndex}
                                                fieldName={fieldName}
                                                fieldValue={fieldValue}
                                                containsHTML={containsHTML}
                                            />
                                        ))}
                                    </div>

                                    {/* Divider between records */}
                                    {recordIndex < taskFields.length - 1 && (
                                        <hr className="my-4" style={{ borderColor: 'var(--hairline)', opacity: 0.5 }} />
                                    )}
                                </div>
                            ))}
                        </div>
                    </motion.div>
                )}
            </AnimatePresence>
        </motion.div>
    )
}
